import { yupResolver } from '@hookform/resolvers/yup';
import { Edit01, InfoCircle } from '@untitled-ui/icons-react/build/cjs';
import { upperCase } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, FormProvider, Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import AdditionalServiceModal from 'src/app/MyQuote/Modals/AdditionalServiceModal';
import DangerousGoodsModal from 'src/app/MyQuote/Modals/DangerousGoodsModal';
import InBondModal from 'src/app/MyQuote/Modals/InBondModal';
import LimitedAccessDeliveryModal from 'src/app/MyQuote/Modals/LimitedAccessDeliveryModal';
import LinearFootageMismatchModal from 'src/app/MyQuote/Modals/LinearFootageMismatchModal';
import LoadInsuranceModal from 'src/app/MyQuote/Modals/LoadInsuranceModal';
import { TAILGATE_SERVICE_IDS } from 'src/app/MyQuote/Modals/quoteDetailModal.const';
import { IDangerousGoodsForm, IInBondForm, ILimitedAccessDeliveryData, ILimitedAccessDeliveryDataForm, ILoadInsuranceForm } from 'src/app/MyQuote/Modals/quoteDetailModal.interface';
import WeightLinearFootageMismatchModal from 'src/app/MyQuote/Modals/WeightLinearFootageMismatchModal';
import WeightMismatchModal from 'src/app/MyQuote/Modals/WeightMismatchModal';
import CheckBox from 'src/components/CheckBox';
import ConfirmModal from 'src/components/ConfirmModal';
import InputText from 'src/components/InputText/InputText';
import TooltipCmp from 'src/components/TooltipCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import { CURRENCY, FOOT, KGS_MAX_VALUES, LBS_MAX_VALUES, MEASUREMENT, QUOTE_CLASSIFICATION } from 'src/constants/common';
import { RootState } from 'src/redux/store';
import { calculateLinearFootage, findServiceTypeIdBySlug, formatLinearFootage, getCalculatedFreightClass } from 'src/utils/CommonFunctions';
import * as yup from 'yup';

import { QUOTE_KEYS } from '../../createQuote.const';
import { completedSteps as completedStepsSelector, createQuoteData, isCreateLoading as isCreateLoadingSelector, quoteDetails } from '../../CreateQuote.slice';
import StepperAction from '../StepperAction';
import { useSaveNextStep } from '../useSaveNextStep';

import LineItem from './LineItem';

const initialQuoteDetailAction = {
    tailgate: false,
    pickupService: false,
    deliveryService: false,
    otherService: false,
    inBond: false,
    loadInsurance: false,
    dangerousGoods: false,
    limitedAccess: false,
    weightMismatch: false,
    linearFootageMismatch: false,
    weightLinearFootageMismatch: false,
    data: null as { [key: string]: any } | null,
};

export interface IDimensions {
    id: number | null;
    handlingUnit: number;
    handlingUnitNo: number;
    weight: number;
    itemLength: number;
    width: number;
    height: number;
    totalWeight: number;
    isStack: boolean;
    commodity: string | null;
    freightClass: number;
    itemClassForDayross: number;
}

export interface IAdditionalServices {
    serviceId?: number[];
    dangerousGoods?: boolean;
    dangerGoodsType?: string;
    description?: string;
    emergencyContactExt?: string;
    emergencyContactName?: string;
    emergencyContactNumber?: string;
    goodsClass?: string;
    inbond?: boolean;
    inbondType?: string;
    inbondName?: string;
    inbondAddress?: string;
    inbondContactMethod?: string;
    inbondContactValue?: string;
    unitedNationsNumber?: string;
    emergencyEapnbr?: string;
    limitedAccess?: boolean;
    limitedAccessType?: string;
    limitedAccessOther?: string;
    packagingGroup?: string;
    insuranceCommercialValue?: string;
    insuranceCurrencyType?: string;
    insuranceCommodity?: string;
}

const isSameDimensions = (dims: Array<Record<string, any>>, defaultDims: Array<Record<string, any>>): boolean => {
    if (dims.length !== defaultDims.length) return false;

    return dims.every((dim: Record<string, any>, i: number) => {
        const def = defaultDims[i];

        return Object.keys(def).every((key) => dim[key] === def[key]);
    });
};

export interface IDimensionsForm {
    serviceId: number[];
    dimensions: IDimensions[];
    totalWeight: string | number | null;
    linearFootage: string | number | null;
    weightMeasurement: string;
    lengthMeasurement: string;
    additionalServices: IAdditionalServices;
}

const defaultDimensions = [
    {
        id: null,
        handlingUnit: 1,
        handlingUnitNo: 0,
        weight: 0,
        itemLength: 0,
        width: 0,
        height: 0,
        totalWeight: 0,
        isStack: true,
        commodity: null,
        freightClass: 0,
        itemClassForDayross: 0,
    },
];

interface AddDimensionsProps {
    setHeaderChildren: (children: React.ReactNode) => void;
}

// eslint-disable-next-line max-lines-per-function
const AddDimensions = ({ setHeaderChildren }: AddDimensionsProps) => {
    const { t } = useTranslation();
    const [action, setAction] = useState(initialQuoteDetailAction);
    const { serviceTypes, services } = useSelector((state: RootState) => state.basic);
    const serviceRefName = useRef<HTMLParagraphElement>(null);
    const quoteData = useSelector(createQuoteData);
    const quoteDetailsData = useSelector(quoteDetails);
    const isLoading = useSelector(isCreateLoadingSelector);
    const completedSteps = useSelector(completedStepsSelector);
    const isRendered = useRef<boolean>(false);
    const hasInitializedServices = useRef<boolean>(false);
    const { onSaveNext, onBack } = useSaveNextStep();
    const classification = useMemo(() => quoteDetailsData?.classification, [quoteDetailsData?.classification]);

    const isAllPreviousStepsCompleted = useMemo(() => {
        const requiredSteps = [QUOTE_KEYS.QUOTE_DETAILS, QUOTE_KEYS.SHIPPER_CONSIGNEE_DETAILS, QUOTE_KEYS.SHIPMENT_DETAILS];

        return requiredSteps.every((step) => completedSteps.includes(step));
    }, [completedSteps]);

    const handleClose = useCallback(() => {
        setAction(initialQuoteDetailAction);
    }, []);

    const isDimensionFieldRequired = function (this: yup.TestContext, value: any) {
        const { handlingUnitNo, itemLength, width, height, weight, totalWeight, freightClass } = this.parent;
        const { dimensions } = this.from?.[1]?.value || {};

        if (dimensions?.length === 1) {
            const hasAnyValue = classification === 'ltl' && (handlingUnitNo >= 1 || itemLength >= 1 || width >= 1 || height >= 1 || weight >= 1 || totalWeight >= 1 || freightClass >= 1);

            if (hasAnyValue) {
                return value !== null && value !== undefined && value >= 1;
            }

            return true;
        }

        if (dimensions?.length > 1) {
            return value !== null && value !== undefined && value >= 1;
        }

        return true;
    };

    const createMaxValueValidator = (maxValueKey: keyof typeof KGS_MAX_VALUES & keyof typeof LBS_MAX_VALUES, translationKey: string) =>
        function (this: yup.TestContext, value: any) {
            const { weightMeasurement } = this.from?.[1]?.value || {};
            const maxValue = weightMeasurement === MEASUREMENT.WEIGHT2.value ? KGS_MAX_VALUES[maxValueKey] : LBS_MAX_VALUES[maxValueKey];

            if (value === null || value === undefined) return true;

            if (value > maxValue) {
                return this.createError({
                    path: `${this.path}`,
                    message: t(translationKey, { max: maxValue } as any),
                });
            }

            return true;
        };

    const transformDimensionValue = (value: any, originalValue: any) => {
        if (originalValue === '' || originalValue === null) return null;

        if (typeof originalValue === 'string') {
            const numericValue = parseFloat(originalValue);

            return isNaN(numericValue) ? null : numericValue;
        }

        return value;
    };

    const schema = yup.object().shape({
        dimensions: yup.array().of(
            yup.object().shape({
                handlingUnit: yup
                    .number()
                    .transform((value, originalValue) => (originalValue === '' ? null : value))
                    .nullable(),

                handlingUnitNo: yup
                    .number()
                    .transform((value, originalValue) => (originalValue === '' ? null : value))
                    .nullable()
                    .test('conditional-required', t('ValidationMessages.This_field_is_required_when_any_dimension_field_is_filled'), isDimensionFieldRequired),

                itemLength: yup
                    .number()
                    .transform(transformDimensionValue)
                    .nullable()
                    .max(9999)
                    .test('conditional-required', t('ValidationMessages.This_field_is_required_when_any_dimension_field_is_filled'), isDimensionFieldRequired),

                width: yup
                    .number()
                    .transform(transformDimensionValue)
                    .nullable()
                    .max(9999)
                    .test('conditional-required', t('ValidationMessages.This_field_is_required_when_any_dimension_field_is_filled'), isDimensionFieldRequired),

                height: yup
                    .number()
                    .transform(transformDimensionValue)
                    .nullable()
                    .max(9999)
                    .test('conditional-required', t('ValidationMessages.This_field_is_required_when_any_dimension_field_is_filled'), isDimensionFieldRequired),

                weight: yup
                    .number()
                    .transform(transformDimensionValue)
                    .nullable()
                    .test('conditional-required', t('ValidationMessages.This_field_is_required_when_any_dimension_field_is_filled'), isDimensionFieldRequired),

                totalWeight: yup
                    .number()
                    .transform(transformDimensionValue)
                    .nullable()
                    .test('max', '', createMaxValueValidator('MAX_WEIGHT', 'ValidationMessages.Total_weight_must_not_exceed'))
                    .test('conditional-required', t('ValidationMessages.This_field_is_required_when_any_dimension_field_is_filled'), isDimensionFieldRequired),

                freightClass: yup
                    .number()
                    .transform(transformDimensionValue)
                    .nullable()
                    .test('conditional-required', t('ValidationMessages.This_field_is_required_when_any_dimension_field_is_filled'), isDimensionFieldRequired),
                commodity: yup
                    .string()
                    .transform((value, originalValue) => (originalValue === '' ? null : value))
                    .nullable(),
                isStack: yup.boolean().nullable(),
            })
        ),
        serviceId: yup.array().of(yup.number()).nullable(),
        totalWeight: yup.string().test('required-for-ftl-ltl', t('ValidationMessages.Total_weight_is_required'), function (value) {
            if (classification === QUOTE_CLASSIFICATION.FTL || classification === QUOTE_CLASSIFICATION.LTL) {
                if (value && Number(value) <= 0) {
                    return false;
                } else {
                    return Boolean(value && value.trim().length > 0);
                }
            }

            return true;
        }),
        linearFootage: yup
            .string()
            .test('required-for-ltl', t('ValidationMessages.Linear_footage_is_required'), function (value) {
                if (classification === QUOTE_CLASSIFICATION.LTL) {
                    return Boolean(value && value.trim().length > 0);
                }

                return true;
            })
            .test(
                'max',
                t('ValidationMessages.Linear_footage_must_not_exceed', {
                    max: 53,
                } as any),
                function (value) {
                    if (!value || value.trim().length === 0) return true;

                    const numericValue = parseFloat(value);

                    if (isNaN(numericValue)) return true;

                    return numericValue <= 53;
                }
            ),
    });

    const defaultValues = useMemo(
        () => ({
            serviceId: quoteData?.dimensionsDetails?.serviceId || [],
            dimensions:
                quoteData?.dimensionsDetails?.dimensions && quoteData?.dimensionsDetails?.dimensions?.length > 0
                    ? quoteData?.dimensionsDetails?.dimensions
                    : classification === QUOTE_CLASSIFICATION.LTL
                    ? []
                    : [],
            weightMeasurement: upperCase(quoteData?.dimensionsDetails?.weightMeasurement) || MEASUREMENT.WEIGHT1.value,
            lengthMeasurement: upperCase(quoteData?.dimensionsDetails?.lengthMeasurement) || MEASUREMENT.LENGTH1.value,
            additionalServices: {
                serviceId: quoteData?.dimensionsDetails?.additionalServices?.serviceId || [],
                dangerousGoods: quoteData?.dimensionsDetails?.additionalServices?.dangerousGoods || false,
                dangerGoodsType: quoteData?.dimensionsDetails?.additionalServices?.dangerGoodsType || '',
                description: quoteData?.dimensionsDetails?.additionalServices?.description || '',
                emergencyContactExt: quoteData?.dimensionsDetails?.additionalServices?.emergencyContactExt || '',
                emergencyContactName: quoteData?.dimensionsDetails?.additionalServices?.emergencyContactName || '',
                emergencyContactNumber: quoteData?.dimensionsDetails?.additionalServices?.emergencyContactNumber || '',
                goodsClass: quoteData?.dimensionsDetails?.additionalServices?.goodsClass || '',
                inbond: quoteData?.dimensionsDetails?.additionalServices?.inbond || false,
                inbondType: quoteData?.dimensionsDetails?.additionalServices?.inbondType || '',
                inbondName: quoteData?.dimensionsDetails?.additionalServices?.inbondName || '',
                inbondAddress: quoteData?.dimensionsDetails?.additionalServices?.inbondAddress || '',
                inbondContactMethod: quoteData?.dimensionsDetails?.additionalServices?.inbondContactMethod || '',
                inbondContactValue: quoteData?.dimensionsDetails?.additionalServices?.inbondContactValue || '',
                unitedNationsNumber: quoteData?.dimensionsDetails?.additionalServices?.unitedNationsNumber || '',
                emergencyEapnbr: quoteData?.dimensionsDetails?.additionalServices?.emergencyEapnbr || '',
                limitedAccess: quoteData?.dimensionsDetails?.additionalServices?.limitedAccess || false,
                limitedAccessType: quoteData?.dimensionsDetails?.additionalServices?.limitedAccessType || '',
                limitedAccessOther: quoteData?.dimensionsDetails?.additionalServices?.limitedAccessOther || '',
                packagingGroup: quoteData?.dimensionsDetails?.additionalServices?.packagingGroup || '',
                insuranceCommercialValue: quoteData?.dimensionsDetails?.additionalServices?.insuranceCommercialValue || '',
                insuranceCurrencyType: quoteData?.dimensionsDetails?.additionalServices?.insuranceCurrencyType || '',
                insuranceCommodity: quoteData?.dimensionsDetails?.additionalServices?.insuranceCommodity || '',
            },
            linearFootage: quoteData?.dimensionsDetails?.linearFootage ? String(quoteData.dimensionsDetails.linearFootage) : '',
            totalWeight: quoteData?.dimensionsDetails?.totalWeight ? String(quoteData.dimensionsDetails.totalWeight) : '',
        }),
        [quoteData?.dimensionsDetails]
    );

    const methods = useForm<IDimensionsForm>({
        resolver: yupResolver(schema) as unknown as Resolver<IDimensionsForm>,
        defaultValues,
    });

    const handleAction = (type: keyof typeof initialQuoteDetailAction, data: { [key: string]: any } | undefined = undefined) => {
        setAction(
            (old) =>
                ({
                    ...old,
                    [type]: true,
                    data: data || (old.data as { [key: string]: any } | null),
                } as typeof initialQuoteDetailAction)
        );
    };

    const {
        setValue,
        watch,
        control,
        trigger,
        handleSubmit,
        getValues,
        formState: { isSubmitted },
        clearErrors,
    } = methods;

    // Linear footage fix: Initialize linearFootage from quoteData when it changes
    useEffect(() => {
        if (quoteData?.dimensionsDetails?.linearFootage) {
            const linearFootageValue = quoteData.dimensionsDetails.linearFootage ? String(quoteData.dimensionsDetails.linearFootage) : '';
            setValue('linearFootage', linearFootageValue);
        }
    }, [quoteData?.dimensionsDetails?.linearFootage, setValue]);

    // Pre-populate pickup/delivery services from shipper/consignee address book on first visit
    useEffect(() => {
        if (hasInitializedServices.current) return;
        hasInitializedServices.current = true;

        // Only auto-populate if user hasn't already saved service selections for this step
        if (!quoteData?.dimensionsDetails?.serviceId?.length) {
            const shipperServiceIds: number[] = (quoteData?.shipperConsigneeDetails?.shipperAddress?.additionalServiceIds || []).map(Number).filter(Boolean);
            const consigneeServiceIds: number[] = (quoteData?.shipperConsigneeDetails?.consigneeAddress?.additionalServiceIds || []).map(Number).filter(Boolean);
            const combined = [...shipperServiceIds, ...consigneeServiceIds];
            const combinedIds = combined.filter((id, index) => combined.indexOf(id) === index);

            if (combinedIds.length > 0) {
                setValue('serviceId', combinedIds);
            }
        }
    }, []);

    const weightMeasurement = watch('weightMeasurement') || MEASUREMENT.WEIGHT1.value;

    const onSubmit = (data: IDimensionsForm) => {
        const dimensionsPayload = {
            weightMeasurement: data?.weightMeasurement || 'lbs',
            lengthMeasurement: data?.lengthMeasurement || 'in',
            // load insurance values
            commodity: data?.additionalServices?.insuranceCommodity || '',
            declaredValue: data?.additionalServices?.insuranceCommercialValue || '',
            declaredValueUnit: data?.additionalServices?.insuranceCurrencyType || '',
            totalWeight: data?.totalWeight || null,
            linearFootage: data?.linearFootage || null,
            dimensions: classification === 'ftl' ? [] : data?.dimensions || [],
            serviceId: data?.serviceId || [],
            servicesData: data?.serviceId?.map((item: any) => services.find((val: any) => val.id === item)) || [],
            additionalServices: {
                serviceId: data?.serviceId || [],
                dangerousGoods: data?.additionalServices?.dangerousGoods || false,
                dangerGoodsType: data?.additionalServices?.dangerGoodsType || '',
                description: data?.additionalServices?.description || '',
                emergencyContactExt: data?.additionalServices?.emergencyContactExt || '',
                emergencyContactName: data?.additionalServices?.emergencyContactName || '',
                emergencyContactNumber: data?.additionalServices?.emergencyContactNumber || '',
                goodsClass: data?.additionalServices?.goodsClass || '',
                inbond: data?.additionalServices?.inbond || false,
                inbondType: data?.additionalServices?.inbondType || '',
                inbondName: data?.additionalServices?.inbondName || '',
                inbondAddress: data?.additionalServices?.inbondAddress || '',
                inbondContactMethod: data?.additionalServices?.inbondContactMethod || '',
                inbondContactValue: data?.additionalServices?.inbondContactValue || '',
                unitedNationsNumber: data?.additionalServices?.unitedNationsNumber || '',
                emergencyEapnbr: data?.additionalServices?.emergencyEapnbr || '',
                limitedAccess: data?.additionalServices?.limitedAccess || false,
                limitedAccessType: data?.additionalServices?.limitedAccessType || '',
                limitedAccessOther: data?.additionalServices?.limitedAccessOther || '',
                packagingGroup: data?.additionalServices?.packagingGroup || '',
            },
        };
        onSaveNext(dimensionsPayload, QUOTE_KEYS.DIMENSIONS);

        return { dimensionsDetails: dimensionsPayload };
    };

    const handleNext = async () => {
        const isValid = await trigger();

        if (!isValid) {
            return false;
        }

        const data = methods.getValues();
        let isTotalWeightOver = false;
        let isItemLengthOver = false;
        const isTailgate = data.serviceId.some((serviceId: number) => TAILGATE_SERVICE_IDS.includes(serviceId));
        const isActionTrue = action.tailgate || action.weightLinearFootageMismatch || action.weightMismatch || action.linearFootageMismatch;

        if (classification === QUOTE_CLASSIFICATION?.LTL && !isActionTrue) {
            let dimensionsToCheck = data.dimensions;
            let totalWeight = 0;

            dimensionsToCheck.forEach((dimension: any) => {
                totalWeight += Number(dimension.totalWeight || 0);
                const maxLengthOver = data?.weightMeasurement === MEASUREMENT.WEIGHT1.value ? LBS_MAX_VALUES.ITEM_LENGTH_OVER : KGS_MAX_VALUES.ITEM_LENGTH_OVER;

                if (dimension.itemLength >= maxLengthOver) {
                    isItemLengthOver = true;
                }
            });

            isTotalWeightOver = data.weightMeasurement === MEASUREMENT.WEIGHT1?.value ? totalWeight >= LBS_MAX_VALUES.TAILGATE_MAX_WEIGHT : totalWeight >= KGS_MAX_VALUES.TAILGATE_MAX_WEIGHT;
        }

        const isTailgateRequired = isTailgate && (isTotalWeightOver || isItemLengthOver) && classification === QUOTE_CLASSIFICATION?.LTL;

        const totalDimensionsWeight = data?.dimensions ? data?.dimensions.reduce((sum: any, item: any) => +sum + (+item.totalWeight || 0), 0) : 0;

        // weight and linear footage check
        const isWeightMismatch = classification === QUOTE_CLASSIFICATION?.LTL && data.totalWeight && totalDimensionsWeight && totalDimensionsWeight != +data.totalWeight && !isActionTrue;

        const totalDimensionsLinearFootageData =
            formatLinearFootage(calculateLinearFootage(data?.dimensions, quoteDetailsData?.equipmentType, false, MEASUREMENT.WEIGHT1?.value, weightMeasurement as any)) || undefined;

        const formLinearFootage = data.linearFootage ? (typeof data.linearFootage === 'string' ? parseFloat(data.linearFootage.replace(/,/g, '')) || 0 : Number(data.linearFootage) || 0) : 0;

        const isLinearFootageMismatch =
            classification === QUOTE_CLASSIFICATION?.LTL && data.linearFootage && totalDimensionsLinearFootageData && totalDimensionsLinearFootageData !== formLinearFootage && !isActionTrue;

        if (isWeightMismatch && isLinearFootageMismatch) {
            handleAction('weightLinearFootageMismatch', {
                totalWeight: totalDimensionsWeight,
                linearFootage: totalDimensionsLinearFootageData,
                weightMeasurement: data.weightMeasurement,
            });

            return false;
        }

        if (isWeightMismatch) {
            handleAction('weightMismatch', {
                totalWeight: totalDimensionsWeight,
                weightMeasurement: data.weightMeasurement,
            });

            return false;
        }

        if (isLinearFootageMismatch) {
            handleAction('linearFootageMismatch', {
                linearFootage: totalDimensionsLinearFootageData,
                weightMeasurement: data.weightMeasurement,
            });

            return false;
        }

        if (isTailgateRequired && !isActionTrue) {
            handleAction('tailgate', {
                weightMeasurement: data.weightMeasurement,
            });

            return false;
        }

        handleSubmit(onSubmit)();

        return true;
    };

    const handleBack = () => {
        const data = getValues();
        onBack(data, QUOTE_KEYS.DIMENSIONS);
    };

    const watchServiceId = watch('serviceId');
    const watchTotalWeight = watch('totalWeight');
    const watchDimensions = watch('dimensions');

    const handleServiceChange = (e: any) => {
        let { checked, name, value } = e.target;
        value = parseInt(value);

        if (checked) {
            if (name === 'in-bond') {
                setAction({ ...action, inBond: true });
            } else if (name === 'load_insurance') {
                setAction({ ...action, loadInsurance: true });
            } else if (name === 'dangerous_goods') {
                setAction({ ...action, dangerousGoods: true });
            } else if (name === 'limited_access_delivery') {
                setAction({ ...action, limitedAccess: true });
            } else {
                setValue('serviceId', [...watchServiceId, value]);
            }
        } else {
            let filteredServices = watchServiceId.filter((item: any) => item !== value);
            setValue('serviceId', filteredServices);

            if (name === 'in-bond') {
                setValue('additionalServices.inbond', false);
                setValue('additionalServices.inbondType', '');
                setValue('additionalServices.inbondName', '');
                setValue('additionalServices.inbondAddress', '');
                setValue('additionalServices.inbondContactMethod', '');
                setValue('additionalServices.inbondContactValue', '');
            } else if (name === 'load_insurance') {
                setValue('additionalServices.insuranceCommercialValue', '');
                setValue('additionalServices.insuranceCurrencyType', CURRENCY.CAD);
                setValue('additionalServices.insuranceCommodity', '');
            } else if (name === 'dangerous_goods') {
                setValue('additionalServices.dangerousGoods', false);
                setValue('additionalServices.dangerGoodsType', '');
                setValue('additionalServices.unitedNationsNumber', '');
                setValue('additionalServices.packagingGroup', '');
                setValue('additionalServices.goodsClass', '');
                setValue('additionalServices.description', '');
                setValue('additionalServices.emergencyContactName', '');
                setValue('additionalServices.emergencyContactNumber', '');
                setValue('additionalServices.emergencyContactExt', '');
                setValue('additionalServices.emergencyEapnbr', '');
            } else if (name === 'limited_access_delivery') {
                setValue('additionalServices.limitedAccess', false);
                setValue('additionalServices.limitedAccessType', '');
                setValue('additionalServices.limitedAccessOther', '');
            }
        }
    };

    const handleEditService = (service: any) => () => {
        if (service?.slug === 'in-bond') {
            setAction({ ...action, inBond: true });
        } else if (service?.slug === 'load_insurance') {
            setAction({ ...action, loadInsurance: true });
        } else if (service?.slug === 'dangerous_goods') {
            setAction({ ...action, dangerousGoods: true });
        } else if (service?.slug === 'limited_access_delivery') {
            setAction({ ...action, limitedAccess: true });
        }
    };

    const textReplacements = [
        {
            original: 'After Hours / Weekend / Holiday Pickup',
            replacement: t('QuotingHub.Common.After_Hours_Pickup'),
        },
        {
            original: 'Pickup Appointment Required',
            replacement: t('QuotingHub.Common.Pickup_Appointment'),
        },
        {
            original: 'Delivery Appointment Required',
            replacement: t('QuotingHub.Common.Delivery_Appointment'),
        },
    ];

    const getReplacedText = (text: string) => {
        const replacementItem = textReplacements?.find((item) => item?.original === text);

        return replacementItem ? replacementItem.replacement : text;
    };

    const EDITABLE_SERVICES_SLUGS = ['in-bond', 'load_insurance', 'dangerous_goods', 'limited_access_delivery'];

    const getList = useCallback(
        (data: any, excludedServices: string[] = []) => (
            <>
                {data.map((val: any) =>
                    !excludedServices.includes(val.slug) &&
                    (val?.type !== 'OTHER' ||
                        classification === QUOTE_CLASSIFICATION.FTL ||
                        val?.slug === 'load_insurance' ||
                        (classification !== QUOTE_CLASSIFICATION.FTL && val.loadLinkTerm !== null)) ? (
                        <div className="flex w-[calc(33.33%_-_8px)]" key={`service_${val.id}`}>
                            <CheckBox
                                id={`service_${val.id}`}
                                onChangeFunc={handleServiceChange}
                                name={val.slug}
                                value={val.id}
                                checked={watchServiceId.includes(val.id) || false}
                                parentClassName="mt-px"
                            />
                            <div className="pl-2 leading-4 text-textSecondary text-xs font-medium truncate flex-1">
                                {val.slug === 'pickup_appointment_required' || val.slug === 'delivery_appointment_required' || val.slug === 'after_hours_pickup' ? (
                                    <TooltipCmp message={val.name} childrenClassName="!flex">
                                        <label htmlFor={`service_${val.id}`} className={`cursor-pointer truncate`}>
                                            {getReplacedText(val.name)}
                                        </label>
                                    </TooltipCmp>
                                ) : (
                                    <TruncateTextCmp message={val.name} refName={serviceRefName}>
                                        <p className="truncate" ref={serviceRefName}>
                                            <label htmlFor={`service_${val.id}`} className={`cursor-pointer`}>
                                                {getReplacedText(val.name)}
                                            </label>
                                        </p>
                                    </TruncateTextCmp>
                                )}
                            </div>
                            {watchServiceId.includes(val.id) && EDITABLE_SERVICES_SLUGS.includes(val.slug) ? (
                                <TooltipCmp message={t('Common.Edit')}>
                                    <Edit01 className="ml-2 mt-px w-3.5 h-3.5 cursor-pointer flex-none hover:text-primary" onClick={handleEditService(val)} />
                                </TooltipCmp>
                            ) : null}
                        </div>
                    ) : (
                        ''
                    )
                )}
            </>
        ),
        [watchServiceId, classification]
    );

    const handleLimitedAccess = (data: ILimitedAccessDeliveryData) => {
        setValue('additionalServices.limitedAccess', true);
        setValue('additionalServices.limitedAccessOther', data.limitedAccessOther);
        setValue('additionalServices.limitedAccessType', data.selectedLimitedAccessType);
        const limitedAccessId = serviceTypes.DELIVERY.find((val: any) => val.slug === 'limited_access_delivery')?.id;

        if (!watchServiceId.includes(Number(limitedAccessId))) {
            setValue('serviceId', [...watchServiceId, Number(limitedAccessId)]);
        }
    };

    const handleDangerousGoods = (data: IDangerousGoodsForm) => {
        const loadInsuranceId = findServiceTypeIdBySlug(serviceTypes.OTHER, 'dangerous_goods');

        if (!watchServiceId.includes(Number(loadInsuranceId))) {
            setValue('serviceId', [...watchServiceId, Number(loadInsuranceId)]);
        }
        setValue('additionalServices.dangerousGoods', true);
        setValue('additionalServices.dangerGoodsType', data.dangerGoodsType);
        setValue('additionalServices.unitedNationsNumber', data.unitedNationsNumber);
        setValue('additionalServices.packagingGroup', data.packagingGroup);
        setValue('additionalServices.goodsClass', data.goodsClass);
        setValue('additionalServices.description', data.description);
        setValue('additionalServices.emergencyContactName', data.emergencyContactName);
        setValue('additionalServices.emergencyContactNumber', data.emergencyContactNumber);
        setValue('additionalServices.emergencyContactExt', data.emergencyContactExt);
        setValue('additionalServices.emergencyEapnbr', data.emergencyEapnbr);
        handleClose();
    };

    const handleInBond = (data: any) => {
        const inBondId = findServiceTypeIdBySlug(serviceTypes.OTHER, 'in-bond');

        if (!watchServiceId.includes(Number(inBondId))) {
            setValue('serviceId', [...watchServiceId, Number(inBondId)]);
        }
        setValue('additionalServices.inbond', true);
        setValue('additionalServices.inbondType', data.inbondType);
        setValue('additionalServices.inbondName', data.inbondName);
        setValue('additionalServices.inbondAddress', data.inbondAddress);
        setValue('additionalServices.inbondContactMethod', data.inbondContactMethod);
        setValue('additionalServices.inbondContactValue', data.inbondContactValue);
        handleClose();
    };

    const handleLoadInsurance = (data: ILoadInsuranceForm) => {
        const loadInsuranceId = findServiceTypeIdBySlug(serviceTypes.OTHER, 'load_insurance');
        setValue('serviceId', [...watchServiceId, Number(loadInsuranceId)]);
        setValue('additionalServices.insuranceCommercialValue', data.insuranceCommercialValue);
        setValue('additionalServices.insuranceCurrencyType', data.insuranceCurrencyType);
        setValue('additionalServices.insuranceCommodity', data.insuranceCommodity);
        handleClose();
    };

    const handleTailgate = (status: boolean) => () => {
        handleClose();

        if (status) {
            handleSubmit(onSubmit)();
        } else {
            handleClose();
        }
    };

    useEffect(() => {
        setHeaderChildren?.(
            <StepperAction
                handleNext={handleSubmit(handleNext)}
                handleBack={handleBack}
                nextButtonText={isAllPreviousStepsCompleted ? t('QuotingHub.Create_Quote') : t('Common.Next')}
                isNextButtonLoading={isAllPreviousStepsCompleted ? isLoading : false}
            />
        );

        return () => {
            setHeaderChildren?.(null);
        };
    }, [isLoading, isAllPreviousStepsCompleted, handleSubmit, handleNext, handleBack, t]);

    const watchLinearFootage = watch('linearFootage');

    useEffect(() => {
        if (watchTotalWeight) {
            clearErrors('totalWeight');
        }
    }, [watchTotalWeight]);

    useEffect(() => {
        if (watchLinearFootage) {
            clearErrors('linearFootage');
        }
    }, [watchLinearFootage]);

    useEffect(() => {
        if (!isRendered.current) {
            isRendered.current = true;

            return;
        }

        const condition = !isSameDimensions(watch('dimensions'), defaultDimensions) && classification !== 'ftl';

        if (condition) {
            const dimensions = watch('dimensions');
            const equipmentType = quoteDetailsData?.equipmentType;

            if (dimensions.length > 0) {
                const calculatedTotalWeight = dimensions.reduce((acc: number, curr: any) => acc + (Number(curr.totalWeight) || 0), 0);
                setValue('totalWeight', calculatedTotalWeight ? String(calculatedTotalWeight) : '');
            }

            // Calculate linearFootage based on dimensions, equipmentType, and weightMeasurement
            const calLinearFootage = formatLinearFootage(calculateLinearFootage(dimensions, equipmentType, false, MEASUREMENT.WEIGHT1?.value, weightMeasurement as any)) || '';

            if (calLinearFootage) {
                setValue('linearFootage', calLinearFootage);
            }

            if (isSubmitted) {
                trigger('linearFootage');
                trigger('totalWeight');
            }
        }
    }, [
        quoteDetailsData?.equipmentType,
        watch('weightMeasurement'),
        JSON.stringify(
            watch('dimensions')?.map((item: any) => ({
                handlingUnitNo: item.handlingUnitNo,
                width: item.width,
                height: item.height,
                itemLength: item.itemLength,
                weight: item.weight,
                totalWeight: item.totalWeight,
                isStack: item.isStack,
            }))
        ),
    ]);

    // Recalculate freight class when weightMeasurement or lengthMeasurement changes
    useEffect(() => {
        const lengthMeasurement = watch('lengthMeasurement');
        const dimensions = watch('dimensions');

        if (dimensions && dimensions.length > 0) {
            dimensions.forEach((dimension: any, index: number) => {
                const weight = dimension.weight;
                const itemLength = dimension.itemLength;
                const width = dimension.width;
                const height = dimension.height;

                if (weight && itemLength && width && height) {
                    const freightData = getCalculatedFreightClass({
                        itemLength: itemLength || 0,
                        width: width || 0,
                        height: height || 0,
                        weight: weight || 0,
                        lengthUnit: lengthMeasurement,
                        weightUnit: weightMeasurement,
                    });

                    const freightClass = freightData?.freightClass || 0;
                    const itemClassForDayross = freightData?.itemClassForDayross || 0;

                    setValue(`dimensions.${index}.freightClass`, freightClass);
                    setValue(`dimensions.${index}.itemClassForDayross`, itemClassForDayross);

                    if (isSubmitted) {
                        trigger(`dimensions.${index}.freightClass`);
                    }
                } else {
                    setValue(`dimensions.${index}.freightClass`, 0);
                }
            });
        }
    }, [
        watch('weightMeasurement'),
        watch('lengthMeasurement'),
        JSON.stringify(
            watch('dimensions')?.map((item: any) => ({
                weight: item.weight,
                itemLength: item.itemLength,
                width: item.width,
                height: item.height,
            }))
        ),
    ]);

    const handleTotalWeightChange = () => {
        const totalWeight = watch('dimensions').reduce((acc, dimension) => acc + Number(dimension.totalWeight || 0), 0);
        setValue('totalWeight', totalWeight ? String(totalWeight) : '');
    };

    useEffect(() => {
        const totalWeightNum = typeof watchTotalWeight === 'string' ? parseFloat(watchTotalWeight) || 0 : watchTotalWeight || 0;

        if ((totalWeightNum <= 0 || watchTotalWeight === undefined || watchTotalWeight === null || watchTotalWeight === '') && watchDimensions && watchDimensions.length > 0) {
            handleTotalWeightChange();
        }
    }, [
        JSON.stringify(
            watchDimensions?.map((item: any) => ({
                totalWeight: item.totalWeight,
                weight: item.weight,
                handlingUnitNo: item.handlingUnitNo,
                itemLength: item.itemLength,
                width: item.width,
                height: item.height,
            }))
        ),
    ]);

    const handleActionClose = () => () => {
        setAction(initialQuoteDetailAction);
    };

    const handleMismatch = (fields: (keyof IDimensionsForm)[]) => () => {
        if (action.data) {
            fields.forEach((field) => {
                setValue(field as any, action.data?.[field]);
            });
        }
        handleClose();
        handleSubmit(onSubmit)();
    };

    return (
        <>
            <FormProvider {...methods}>
                <div className="stepper-content-container ">
                    <div className="stepper-lable-wrap">
                        <label className="stepper-label-common">
                            {t('Common.Load_Overview')}
                            <span className="text-red-600 ml-[2px] leading-4 text-xs">*</span>
                        </label>
                        {classification === QUOTE_CLASSIFICATION.LTL ? <p className="text-textSecondary text-xs font-normal">{t('Common.Auto_Calculated')}</p> : <></>}
                    </div>
                    <div className="flex items-center gap-x-2 flex-1">
                        <Controller
                            control={control}
                            name="totalWeight"
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <InputText
                                    inputName="totalWeight"
                                    placeholder={t('QuotingHub.Common.Enter_total_weight')}
                                    parentClassName="flex-1"
                                    value={value || ''}
                                    onChangeFunc={onChange}
                                    inputType="number"
                                    isAllowDecimal={true}
                                    required={classification !== QUOTE_CLASSIFICATION.FTL}
                                    shouldFormatNumber={true}
                                    maxLength={7}
                                    errorText={error?.message && error?.type === 'max' ? error?.message : !!error}
                                    isError={!!error?.message}
                                    inputValueUnitType={watch('weightMeasurement')?.toLowerCase()}
                                    autoFocus={true}
                                />
                            )}
                        />
                        {classification === QUOTE_CLASSIFICATION.LTL && (
                            <Controller
                                control={control}
                                name="linearFootage"
                                render={({ field: { onChange, value }, fieldState: { error } }) => (
                                    <InputText
                                        inputName="linearFootage"
                                        placeholder={t('QuotingHub.Common.Enter_linear_footage')}
                                        parentClassName="flex-1"
                                        value={value || ''}
                                        onChangeFunc={onChange}
                                        required={classification !== QUOTE_CLASSIFICATION.FTL}
                                        shouldFormatNumber={true}
                                        maxLength={2}
                                        inputValueUnitType="ft"
                                        errorText={error?.message && error?.type === 'max' ? error?.message : ''}
                                        isIconError={!!error?.message}
                                        inputType="number"
                                        isAllowDecimal={false}
                                        isError={!!error}
                                        disabled={classification === 'ftl'}
                                    />
                                )}
                            />
                        )}
                    </div>
                </div>
                {classification === QUOTE_CLASSIFICATION.LTL ? (
                    <div className="stepper-content-container w-full flex flex-col border-t border-utilityGray200">
                        <LineItem />
                    </div>
                ) : null}

                <div className="border-t border-utilityGray200 stepper-content-container">
                    <div className="stepper-lable-wrap flex">
                        <label className="stepper-label-common flex items-center gap-x-0.5">
                            {t('QuotingHub.Common.Pickup_Additional_Services')}
                            <div className={`cursor-pointer`} onClick={() => setAction({ ...action, pickupService: true })}>
                                <InfoCircle className="h-3.5 w-3.5 cursor-pointer text-primary" />
                            </div>
                        </label>
                    </div>
                    <div className="flex-1">
                        <div className="rounded-lg border border-utilityGray200 bg-white flex flex-wrap p-3 gap-3">{getList(serviceTypes?.PICKUP || [])}</div>
                    </div>
                </div>

                <div className="border-t border-utilityGray200 stepper-content-container">
                    <div className="stepper-lable-wrap flex">
                        <label className="stepper-label-common flex items-center gap-x-0.5">
                            {t('QuotingHub.Common.Delivery_Additional_Services')}
                            <div className={`cursor-pointer`} onClick={() => setAction({ ...action, deliveryService: true })}>
                                <InfoCircle className="h-3.5 w-3.5 cursor-pointer text-primary" />
                            </div>
                        </label>
                    </div>
                    <div className="flex-1 space-y-5">
                        <div className="rounded-lg border border-utilityGray200 bg-white flex flex-wrap p-3 gap-3">{getList(serviceTypes?.DELIVERY || [])}</div>
                    </div>
                </div>

                <div className="border-t border-utilityGray200 stepper-content-container border-b pb-5">
                    <div className="stepper-lable-wrap flex">
                        <label className="stepper-label-common flex items-center gap-x-0.5">
                            {t('Common.Other_Additional_Services')}
                            <div className={`cursor-pointer`} onClick={() => setAction({ ...action, otherService: true })}>
                                <InfoCircle className="h-3.5 w-3.5 cursor-pointer text-primary" />
                            </div>
                        </label>
                    </div>
                    <div className="flex-1 space-y-5">
                        <div className="rounded-lg border border-utilityGray200 bg-white flex flex-wrap p-3 gap-3">{getList(serviceTypes?.OTHER || [], ['load_insurance'])}</div>
                    </div>
                </div>

                {(action.pickupService || action.deliveryService || action.otherService) && (
                    <AdditionalServiceModal
                        handleClose={handleClose}
                        services={
                            (action.otherService ? (serviceTypes?.OTHER || []).filter((s: any) => s.slug !== 'load_insurance') : serviceTypes?.[action.pickupService ? 'PICKUP' : 'DELIVERY']) || []
                        }
                        title={`${action.pickupService ? 'Pickup' : action.deliveryService ? 'Delivery' : 'Other'} ${t('Common.Additional_Services')} `}
                    />
                )}

                {action.inBond && <InBondModal handleClose={handleClose} handleInBond={handleInBond} defaultFormValues={watch('additionalServices') as IInBondForm} />}
                {action.loadInsurance && (
                    <LoadInsuranceModal handleClose={handleClose} handleLoadInsurance={handleLoadInsurance} defaultFormValues={watch('additionalServices') as ILoadInsuranceForm} />
                )}
                {action.dangerousGoods && (
                    <DangerousGoodsModal handleClose={handleClose} handleDangerousGoods={handleDangerousGoods} defaultFormValues={watch('additionalServices') as IDangerousGoodsForm} />
                )}
                {action.limitedAccess && (
                    <LimitedAccessDeliveryModal
                        handleClose={handleClose}
                        handleLimitedAccess={handleLimitedAccess}
                        defaultFormValues={watch('additionalServices') as ILimitedAccessDeliveryDataForm | null}
                    />
                )}

                {action.tailgate && (
                    <ConfirmModal
                        title={t('QuotingHub.Common.Tailgate_Over_Dimensions')}
                        description={t('QuotingHub.QuotingDashboard.tailgate_warning', {
                            length: FOOT,
                            weight: action.data && watch('weightMeasurement') === MEASUREMENT.WEIGHT1.value ? `${LBS_MAX_VALUES.TAILGATE_MAX_WEIGHT}lbs` : `${KGS_MAX_VALUES.TAILGATE_MAX_WEIGHT}kgs`,
                        })}
                        button1={t('Common.confirm_yes')}
                        button2={t('Common.Modify_Selection')}
                        handleClose={handleTailgate}
                    />
                )}

                {action.weightMismatch && <WeightMismatchModal handleActionClose={handleActionClose} handleMismatch={handleMismatch(['totalWeight'])} data={action.data} />}
                {action.linearFootageMismatch && <LinearFootageMismatchModal handleActionClose={handleActionClose} handleMismatch={handleMismatch(['linearFootage'])} data={action.data} />}
                {action.weightLinearFootageMismatch && (
                    <WeightLinearFootageMismatchModal handleActionClose={handleActionClose} handleMismatch={handleMismatch(['totalWeight', 'linearFootage'])} data={action.data} />
                )}
            </FormProvider>
        </>
    );
};

export default AddDimensions;
